import { useEffect } from "react";
import { useTranslation } from "react-i18next";

import type { Folder } from "@/api/types";
import { Button } from "@/components/ui/button";
import { FolderEmployeesPanel } from "@/components/folder/FolderEmployeesPanel";
import { FolderMembersPanel } from "@/components/folder/FolderMembersPanel";

/**
 * Right-side drawer that surfaces a folder's membership + employee
 * curation for the admin (TaskMe 45826db6: "А где права для
 * пользователей? как назначать папки?").
 *
 * Implementation notes:
 *   * Pure CSS slide-over rather than a portal — avoids pulling in
 *     another Radix primitive when the visual contract is just "fixed
 *     panel on the right with an overlay". Mirrors how the existing
 *     `Dialog` is consumed inline.
 *   * Body scroll is locked while the drawer is open so the page
 *     underneath does not scroll-jack the slide-over content.
 *   * The drawer's child panels each own their own data fetches; the
 *     drawer itself only owns presentation (header + close + scroll
 *     container). Keeping concerns split means a future "folder
 *     transfer history" tab can be added by composing it next to the
 *     existing panels without re-threading shared state.
 */
interface FolderDrawerProps {
  open: boolean;
  folder: Folder | null;
  onClose: () => void;
}

export function FolderDrawer({ open, folder, onClose }: FolderDrawerProps) {
  const { t } = useTranslation();

  // Lock body scroll while open. Restored on close OR unmount (cleanup
  // covers the case where the parent unmounts the drawer mid-open, e.g.
  // a route change).
  useEffect(() => {
    if (!open) return undefined;
    const previous = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = previous;
    };
  }, [open]);

  // Esc closes the drawer — matches user expectation for a slide-over
  // and removes the keyboard trap risk for screen-reader users.
  useEffect(() => {
    if (!open) return undefined;
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [open, onClose]);

  if (!open || folder === null) return null;

  return (
    <div
      className="fixed inset-0 z-50"
      data-testid="folder-drawer"
      role="dialog"
      aria-modal="true"
      aria-label={t("folders.admin.drawerTitle", { name: folder.name })}
    >
      <div
        className="absolute inset-0 bg-black/50"
        data-testid="folder-drawer-overlay"
        onClick={onClose}
      />
      <aside
        className="absolute right-0 top-0 flex h-full w-full max-w-2xl flex-col gap-4 overflow-y-auto border-l bg-background p-6 shadow-xl"
        data-testid="folder-drawer-panel"
      >
        <header className="flex items-start justify-between gap-2">
          <div className="space-y-1">
            <h2
              className="text-lg font-semibold"
              data-testid="folder-drawer-title"
            >
              {t("folders.admin.drawerTitle", { name: folder.name })}
            </h2>
            {folder.is_default ? (
              <span
                className="inline-flex items-center rounded-full border border-emerald-500/30 bg-emerald-500/10 px-2 py-0.5 text-xs font-medium text-emerald-700 dark:text-emerald-300"
                data-testid="folder-drawer-default-badge"
              >
                {t("folders.admin.defaultBadge")}
              </span>
            ) : null}
          </div>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={onClose}
            data-testid="folder-drawer-close"
          >
            {t("folders.admin.drawerClose")}
          </Button>
        </header>

        <FolderMembersPanel folder={folder} />
        <FolderEmployeesPanel folder={folder} />
      </aside>
    </div>
  );
}
